export default function Billing({ profile, t }) {
  const currentPlan = profile?.plan || 'free'

  const PLANS = [
    {
      id: 'free',
      name: 'Starter',
      price: 'Rs.0',
      period: '/mo',
      tagline: 'Perfect for trying out Employee Hive',
      employees: 'Up to 3 employees',
      color: t.textMuted,
      features: [
        'Daily check-ins & check-outs',
        'Goals + PDR cycles',
        'Employee profiles',
        'Basic reporting',
      ],
      cta: 'Current plan',
      isCurrent: currentPlan === 'free',
      highlighted: false,
    },
    {
      id: 'seed',
      name: 'Seed',
      price: 'Rs.500',
      period: '/mo',
      tagline: 'For growing teams with full HR needs',
      employees: 'Up to 10 employees',
      color: t.primary,
      features: [
        'Everything in Starter',
        'Meetings & scheduling',
        'Performance reviews',
        'Review reports & sign-off',
        'Email support',
      ],
      cta: 'Upgrade to Seed',
      isCurrent: currentPlan === 'seed',
      highlighted: true,
      badge: 'Most Popular',
    },
    {
      id: 'growth',
      name: 'Growth',
      price: 'Rs.1000',
      period: '/mo',
      tagline: 'For larger teams needing advanced features',
      employees: 'Up to 30 employees',
      color: t.purple,
      features: [
        'Everything in Seed',
        'Unlimited employees',
        'Priority support',
        'Export all reports',
        'Custom branding',
        'Dedicated account manager',
      ],
      cta: 'Upgrade to Growth',
      isCurrent: currentPlan === 'growth',
      highlighted: false,
    },
  ]

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: t.text, margin: '0 0 4px', letterSpacing: '-0.5px' }}>
          Billing & Plans
        </h1>
        <p style={{ fontSize: 14, color: t.textMuted, margin: 0 }}>
          Manage your subscription and usage
        </p>
      </div>

      {/* Current plan banner */}
      <div style={{ background: `${t.primary}10`, border: `1px solid ${t.primary}25`, borderRadius: 12, padding: '14px 20px', marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primary2} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 16 }}>💳</span>
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>
              You're on the <span style={{ color: t.primary, textTransform: 'capitalize' }}>{currentPlan}</span> plan
            </div>
            <div style={{ fontSize: 12, color: t.textMuted }}>
              {currentPlan === 'free' ? 'Upgrade to unlock more features' : 'Thank you for your subscription'}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 12, fontWeight: 600, padding: '5px 14px', borderRadius: 99, background: `${t.primary}14`, color: t.primary, border: `1px solid ${t.primary}30`, textTransform: 'capitalize' }}>
          {currentPlan} Plan
        </div>
      </div>

      {/* Plan cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: '2rem' }}>
        {PLANS.map(plan => (
          <div key={plan.id} style={{
            background: t.card,
            border: `2px solid ${plan.highlighted ? t.primary : plan.isCurrent ? `${t.primary}40` : t.cardBorder}`,
            borderRadius: 16,
            padding: '1.75rem',
            boxShadow: plan.highlighted ? `0 4px 24px ${t.primary}20` : t.shadow,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            transition: 'box-shadow .2s',
          }}>

            {/* Badge */}
            {plan.badge && (
              <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: `linear-gradient(135deg, ${t.primary} 0%, ${t.primary2} 100%)`, color: '#fff', fontSize: 11, fontWeight: 700, padding: '4px 14px', borderRadius: 99, whiteSpace: 'nowrap', boxShadow: `0 2px 8px ${t.primary}40` }}>
                ⭐ {plan.badge}
              </div>
            )}

            {plan.isCurrent && (
              <div style={{ position: 'absolute', top: 14, right: 14, fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 99, background: `${t.success}14`, color: t.success, border: `1px solid ${t.success}30` }}>
                ✓ Active
              </div>
            )}

            {/* Plan name */}
            <div style={{ fontSize: 13, fontWeight: 700, color: plan.highlighted ? t.primary : t.textMuted, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>
              {plan.name}
            </div>

            {/* Price */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, marginBottom: 4 }}>
              <span style={{ fontSize: 32, fontWeight: 900, color: t.text, letterSpacing: '-1px' }}>{plan.price}</span>
              <span style={{ fontSize: 14, color: t.textMuted, marginBottom: 4, fontWeight: 500 }}>{plan.period}</span>
            </div>

            {/* Tagline */}
            <div style={{ fontSize: 13, color: t.textMuted, marginBottom: 6, lineHeight: 1.5 }}>{plan.tagline}</div>

            {/* Employee limit badge */}
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: plan.highlighted ? t.primary : t.textSub, background: plan.highlighted ? `${t.primary}10` : t.dark ? 'rgba(255,255,255,0.05)' : '#F1F5F9', padding: '4px 10px', borderRadius: 99, marginBottom: '1.25rem', alignSelf: 'flex-start' }}>
              👥 {plan.employees}
            </div>

            {/* Divider */}
            <div style={{ height: 1, background: t.border, marginBottom: '1.25rem' }} />

            {/* Features */}
            <div style={{ flex: 1, marginBottom: '1.5rem' }}>
              {plan.features.map(f => (
                <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 9 }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: plan.highlighted ? `${t.primary}14` : `${t.success}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <span style={{ fontSize: 10, color: plan.highlighted ? t.primary : t.success, fontWeight: 800 }}>✓</span>
                  </div>
                  <span style={{ fontSize: 13, color: t.textSub, lineHeight: 1.5 }}>{f}</span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            {plan.isCurrent ? (
              <div style={{ width: '100%', padding: '12px', background: t.dark ? 'rgba(255,255,255,0.05)' : '#F1F5F9', border: `1px solid ${t.border}`, borderRadius: 10, fontSize: 14, fontWeight: 600, color: t.textMuted, textAlign: 'center', boxSizing: 'border-box' }}>
                ✓ Current Plan
              </div>
            ) : (
              <button
                onClick={() => alert(`To upgrade to ${plan.name}, please contact support or visit our website.`)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: plan.highlighted
                    ? `linear-gradient(135deg, ${t.primary} 0%, ${t.primary2} 100%)`
                    : `${t.primary}10`,
                  border: plan.highlighted ? 'none' : `1px solid ${t.primary}30`,
                  borderRadius: 10,
                  fontSize: 14,
                  fontWeight: 700,
                  color: plan.highlighted ? '#fff' : t.primary,
                  cursor: 'pointer',
                  fontFamily: 'Inter, system-ui',
                  boxShadow: plan.highlighted ? `0 2px 12px ${t.primary}30` : 'none',
                  boxSizing: 'border-box',
                  transition: 'all .15s',
                }}>
                {plan.cta} →
              </button>
            )}
          </div>
        ))}
      </div>

      {/* FAQ / info row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        {[
          { icon: '🔒', title: 'Secure payments', desc: 'All billing is handled securely. Cancel anytime, no questions asked.' },
          { icon: '📊', title: 'Usage included', desc: 'Unlimited PDR cycles, check-ins, meetings and reviews on all paid plans.' },
          { icon: '💳', title: 'Transparent pricing', desc: 'Prices are in Rupees (₹). GST applicable as per government rules.' },
        ].map(item => (
          <div key={item.title} style={{ background: t.card, border: `1px solid ${t.cardBorder}`, borderRadius: 12, padding: '1rem 1.25rem', boxShadow: t.shadow, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ fontSize: 22, flexShrink: 0 }}>{item.icon}</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: t.text, marginBottom: 4 }}>{item.title}</div>
              <div style={{ fontSize: 12, color: t.textMuted, lineHeight: 1.6 }}>{item.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
